import type { AuthResponse } from '@timato/shared';

export const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

export class ApiError extends Error {
  constructor(public status: number, public code: string, message?: string, public body?: Record<string, unknown>) { super(message ?? code); }
}

type TokenSource = { getAccessToken: () => string | null; /** Epoch ms the access token stops working, when known. */ getExpiresAt: () => number | null; onRefreshed: (r: AuthResponse) => void; onSignedOut: () => void };
let source: TokenSource | null = null;
export const bindTokenSource = (s: TokenSource) => { source = s; };

/** Refresh this far ahead of expiry, so a request never goes out on a token about to lapse and comes back 401. */
const EARLY_MS = 30_000;
const LOCK = 'timato-auth-refresh';

let refreshing: Promise<AuthResponse | null> | null = null;
/**
 * One refresh in flight at a time, in this tab and across tabs. Every caller in a tab shares one request, and the tabs
 * take turns through a Web Lock: the refresh token is rotated on every use and lives in one cookie they all share, so
 * two tabs refreshing at once meant one presented a token the other had just spent, was refused, and signed out (TW-2).
 * Taking turns, the second tab refreshes with the cookie the first one just received.
 */
export function refreshSession(): Promise<AuthResponse | null> {
  const run = async () => {
    try {
      const res = await fetch(`${API_BASE}/v1/auth/refresh`, { method: 'POST', credentials: 'include' });
      if (!res.ok) { source?.onSignedOut(); return null; }
      const data = (await res.json()) as AuthResponse;
      source?.onRefreshed(data);
      return data;
    } catch { return null; } // offline or unreachable: not a verdict on the session, so nobody is signed out
  };
  if (refreshing) return refreshing;
  const turn = typeof navigator !== 'undefined' && navigator.locks ? navigator.locks.request(LOCK, run).then((r) => r) : run();
  const pending = turn.finally(() => { refreshing = null; });
  refreshing = pending;
  return pending;
}

const expiresSoon = () => { const at = source?.getExpiresAt(); return at != null && at - Date.now() < EARLY_MS; };

/** JSON fetch with bearer auth, cookies, and one transparent retry after refreshing an expired access token. */
export async function api<T>(path: string, init: RequestInit & { json?: unknown; auth?: boolean } = {}): Promise<T> {
  const attempt = async (token: string | null) => fetch(`${API_BASE}${path}`, {
    ...init, credentials: 'include',
    headers: { ...(init.json !== undefined ? { 'Content-Type': 'application/json' } : {}), ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(init.headers ?? {}) },
    body: init.json !== undefined ? JSON.stringify(init.json) : init.body,
    method: init.method ?? (init.json !== undefined ? 'POST' : 'GET'),
  });
  // A token about to lapse is renewed first. A tab left open for hours (or woken from sleep) then never shows the
  // 15-minute 401 in the console, and the 401 path below is left for what the clock cannot see: a revoked device.
  if (init.auth !== false && source?.getAccessToken() && expiresSoon()) await refreshSession();
  let res = await attempt(init.auth === false ? null : source?.getAccessToken() ?? null);
  if (res.status === 401 && init.auth !== false) {
    const refreshed = await refreshSession();
    if (refreshed) res = await attempt(refreshed.accessToken);
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; message?: string } & Record<string, unknown>;
    throw new ApiError(res.status, body.error ?? 'error', body.message, body);
  }
  return (res.status === 204 ? undefined : await res.json()) as T;
}
